import { EventBus } from 'components/eventBus';
import { TDeepPartial } from 'types/deepPartial';

import { getFieldSpecificEventName, TConfigUpdatedKey } from './ConfigProvider.events';

export type TConfigUpdatedHandlerData<TConfig, TConfigCurrent> = {
  updatedValues: TDeepPartial<TConfigCurrent>;
  newConfig: TConfig;
};

export type TConfigUpdatedHandler<TConfig, TConfigCurrent> = (
  data: TConfigUpdatedHandlerData<TConfig, TConfigCurrent>,
) => void;

export const createConfigConsumerScope = (fallbackEventBus: EventBus<any>) => {
  let globalConfig = {};

  const configConsumerConstructor = class ConfigConsumer<
    TConfig,
    TConfigCurrent extends TConfig[keyof TConfig],
    TEventBus extends EventBus<any>,
  > {
    public config: TConfigCurrent;

    public readonly _onConfigUpdatedListener: string;
    public readonly _onConfigUpdatedWithContext: TConfigUpdatedHandler<TConfig, TConfigCurrent> =
      this._onConfigUpdated.bind(this);
    public readonly _configKey: keyof TConfig;

    public readonly _eventBus: TEventBus;

    constructor(originalClassName: keyof TConfig, eventBus: TEventBus) {
      this._configKey = originalClassName;
      this._eventBus = eventBus || fallbackEventBus;

      const correspondingSubscriptionName: TConfigUpdatedKey =
        getFieldSpecificEventName(originalClassName);

      this._onConfigUpdatedListener = this._eventBus.subscribe(
        correspondingSubscriptionName,
        this._onConfigUpdatedWithContext,
      );

      this.config = (globalConfig as TConfig)[originalClassName as keyof TConfig] as TConfigCurrent;
    }

    public destroy(): void {
      this._eventBus.unsubscribe(this._onConfigUpdatedListener);
    }

    public _handleConfigUpdated(
      _updatedValues: TDeepPartial<TConfigCurrent>,
      _newConfig: TConfigCurrent,
    ): void {}

    public _onConfigUpdated(data: TConfigUpdatedHandlerData<TConfig, TConfigCurrent>): void {
      (globalConfig as TConfig) = data.newConfig;
      const newCurrentConfig = data.newConfig[this._configKey as keyof TConfig] as TConfigCurrent;
      this.config = newCurrentConfig;
      this._handleConfigUpdated(data.updatedValues, newCurrentConfig);
    }
  };

  const setConsumerConfig: (config: any) => void = (config: object) => {
    globalConfig = config;
  };

  return {
    configConsumerConstructor,
    setConsumerConfig,
  };
};

export interface IConfigConsumer<TConfigCurrent> {
  config: TConfigCurrent;
  destroy(): void;
  _handleConfigUpdated(
    _updatedValues: TDeepPartial<TConfigCurrent>,
    _newConfig: TConfigCurrent,
  ): void;
}
